import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * Service for managing JSON-LD Structured Data
 *
 * Structured Data helps search engines understand your content and can result in:
 * - Rich Results (breadcrumbs, ratings, etc.)
 * - Better CTR (20-30% increase)
 * - Featured Snippets
 * - Knowledge Panels
 *
 * @see https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data
 */
@Injectable({
  providedIn: 'root'
})
export class StructuredDataService {
  private readonly SCRIPT_TYPE = 'application/ld+json';
  private readonly SCRIPT_ID_PREFIX = 'structured-data-';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Add a JSON-LD script to the document head
   * @param schemaType - Type of schema (e.g., 'organization', 'article')
   * @param schema - The JSON-LD schema object
   */
  addStructuredData(schemaType: string, schema: any): void {
    // Remove existing schema of this type first (prevents duplicates)
    this.removeStructuredData(schemaType);

    const script = this.document.createElement('script');
    script.type = this.SCRIPT_TYPE;
    script.id = `${this.SCRIPT_ID_PREFIX}${schemaType}`;
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  /**
   * Remove a specific JSON-LD script from the document
   * @param schemaType - Type of schema to remove
   */
  removeStructuredData(schemaType: string): void {
    const existingScript = this.document.getElementById(`${this.SCRIPT_ID_PREFIX}${schemaType}`);
    if (existingScript) {
      existingScript.remove();
    }
  }

  /**
   * Remove all structured data scripts
   */
  removeAllStructuredData(): void {
    const scripts = this.document.querySelectorAll(`script[id^="${this.SCRIPT_ID_PREFIX}"]`);
    scripts.forEach(script => script.remove());
  }

  // ========================================
  // LOCAL BUSINESS SCHEMA
  // ========================================

  /**
   * Add LocalBusiness schema for company identity
   * Appears in Google Knowledge Panel and local search results
   */
  addOrganizationSchema(): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'Pezeta Solutions',
      'url': 'https://chatbot.pezetasolutions.com',
      'logo': 'https://chatbot.pezetasolutions.com/images/gray-logo-80.webp',
      'description': 'Empresa líder en desarrollo de software personalizado, sistemas ERP y aplicaciones web en Los Mochis, Sinaloa, México',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Los Mochis',
        'addressRegion': 'Sinaloa',
        'addressCountry': 'MX'
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': '25.7930',
        'longitude': '-108.9855'
      },
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+52-668-856-6833',
        'contactType': 'customer service',
        'email': 'contacto@pezetasolutions.com',
        'areaServed': 'MX',
        'availableLanguage': ['es', 'en']
      },
      'areaServed': ['Los Mochis', 'Sinaloa', 'México'],
      'priceRange': '$$',
      'openingHours': 'Mo-Fr 09:00-18:00',
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '5.0',
        'reviewCount': '50'
      },
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Servicios de Desarrollo de Software',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Desarrollo de Software Personalizado',
              'description': 'Sistemas de software a medida para empresas'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Sistemas ERP',
              'description': 'Sistemas de planificación de recursos empresariales'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Desarrollo de Aplicaciones Web',
              'description': 'Aplicaciones web modernas y responsivas'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Desarrollo de Aplicaciones Móviles',
              'description': 'Apps nativas e híbridas para iOS y Android'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Tiendas eCommerce',
              'description': 'Desarrollo de tiendas online completas'
            }
          }
        ]
      }
    };

    this.addStructuredData('organization', schema);
  }

  // ========================================
  // WEBSITE SCHEMA
  // ========================================

  /**
   * Add WebSite schema with search functionality
   * Enables Google Sitelinks Search Box
   */
  addWebSiteSchema(): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Pezeta Solutions',
      'url': 'https://chatbot.pezetasolutions.com',
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': 'https://chatbot.pezetasolutions.com/blogs?q={search_term_string}'
        },
        'query-input': 'required name=search_term_string'
      }
    };

    this.addStructuredData('website', schema);
  }

  // ========================================
  // BREADCRUMB SCHEMA
  // ========================================

  /**
   * Add BreadcrumbList schema for navigation
   * Displays breadcrumbs in search results
   * @param breadcrumbs - Array of breadcrumb items
   */
  addBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>): void {
    const itemListElement = breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': breadcrumb.name,
      'item': breadcrumb.url
    }));

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': itemListElement
    };

    this.addStructuredData('breadcrumb', schema);
  }

  // ========================================
  // ARTICLE SCHEMA
  // ========================================

  /**
   * Add Article schema for blog posts
   * Enables rich results for articles
   * @param article - Article metadata
   */
  addArticleSchema(article: {
    headline: string;
    description: string;
    url: string;
    datePublished: string;
    dateModified?: string;
    author?: string;
    image?: string;
    category?: string;
  }): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': article.headline,
      'description': article.description,
      'url': article.url,
      'datePublished': article.datePublished,
      'dateModified': article.dateModified || article.datePublished,
      'author': {
        '@type': 'Organization',
        'name': article.author || 'Pezeta Solutions'
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Pezeta Solutions',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://chatbot.pezetasolutions.com/images/logo.png'
        }
      },
      'image': article.image || 'https://chatbot.pezetasolutions.com/images/default-blog-image.jpg',
      'articleSection': article.category || 'Tecnología',
      'inLanguage': 'es-MX'
    };

    this.addStructuredData('article', schema);
  }

  // ========================================
  // SERVICE SCHEMA
  // ========================================

  /**
   * Add Service schema for service pages
   * Helps Google understand your service offerings
   * @param service - Service metadata
   */
  addServiceSchema(service: {
    name: string;
    description: string;
    url: string;
    category?: string;
  }): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': service.name,
      'description': service.description,
      'url': service.url,
      'provider': {
        '@type': 'Organization',
        'name': 'Pezeta Solutions',
        'url': 'https://chatbot.pezetasolutions.com'
      },
      'areaServed': {
        '@type': 'Country',
        'name': 'México'
      },
      'serviceType': service.category || 'Desarrollo de Software'
    };

    this.addStructuredData('service', schema);
  }

  // ========================================
  // FAQ SCHEMA
  // ========================================

  /**
   * Add FAQPage schema
   * Displays FAQ rich results in search
   * @param faqs - Array of FAQ items
   */
  addFAQSchema(faqs: ReadonlyArray<{ question: string; answer: string }>): void {
    const mainEntity = faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }));

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': mainEntity
    };

    this.addStructuredData('faq', schema);
  }
}
